import PropTypes from 'prop-types';
import { getRequestTypeForActions } from '../triggers/requests';
import { callTriggerAction } from '../../actions/custom-triggers';
import { buildActionPayload } from '../triggers/payload';
import interactiveConfig from '../triggers/triggers-config';
import { checkConditionAsync } from '../triggers/conditions';
import { asyncForEach } from '../utils/async-for-each';
import OrganizationService from '../../services/org-service';
import {
  getDeepLinkIfPresent,
  isTrustedDeepLink,
  removeSignupRedirect,
} from '../../services/signup-service';
import { requestHandoffToken } from '../../services/handoff-service';

// Persists the handoff token across TriggerComponent instances (one new instance per reducer
// dispatch). For multi-step flows (Wales/NI go through the questionnaire before the last step),
// a logout trigger may clear the Django session at an intermediate step. Caching the token at
// the earliest point where the session is still active lets the last step retrieve it even after
// the session is gone.
const HANDOFF_TOKEN_CACHE_KEY = 'sl-handoff-token';

class TriggerComponent {
  constructor() {
    this.totalCallsToMake = 0;
    this.numberOfMadeCalls = 0;
    this.triggers = [];
    this.redirectURLs = [];
    this.hasFailed = false;
    this.handoffToken = null;
  }

  // Mint the mobile handoff token BEFORE the step's triggers fire. One of the
  // last-step triggers logs the user out (clearing cookies), and minting requires an
  // authenticated session — so minting after the triggers run would 403. We capture the
  // token here while still authenticated and append it to the deep link at redirect time.
  // The minted token is an independent credential that survives the web-session logout.
  // No-op for the existing web flow (no sleepio:// deep link present), so it cannot affect it.
  async prefetchHandoffToken() {
    const storedDeepLink = getDeepLinkIfPresent();
    // eslint-disable-next-line no-console
    console.log('[TC] prefetchHandoffToken — storedDeepLink:', storedDeepLink, '| sessionStorage cache:', sessionStorage.getItem(HANDOFF_TOKEN_CACHE_KEY));
    this.handoffToken = null;
    if (storedDeepLink) {
      const fresh = await requestHandoffToken();
      // eslint-disable-next-line no-console
      console.log('[TC] prefetchHandoffToken — fresh token result:', fresh ? `minted (${fresh.slice(0, 8)}...)` : 'NULL (session may be gone)');
      if (fresh) {
        sessionStorage.setItem(HANDOFF_TOKEN_CACHE_KEY, fresh);
        this.handoffToken = fresh;
      } else {
        this.handoffToken = sessionStorage.getItem(HANDOFF_TOKEN_CACHE_KEY);
        // eslint-disable-next-line no-console
        console.log('[TC] prefetchHandoffToken — cache fallback:', this.handoffToken ? `found (${this.handoffToken.slice(0, 8)}...)` : 'EMPTY');
      }
    }
  }

  async fireTriggers(triggers, isLastStep = false) {
    this.isLastStep = isLastStep;
    this.triggers = triggers;
    const totalActions = triggers.reduce((n, t) => n + (t.action_ids || []).length, 0);
    // eslint-disable-next-line no-console
    console.log('[TC] fireTriggers — isLastStep:', isLastStep, '| triggers:', triggers.length, '| totalActions:', totalActions);
    await this.prefetchHandoffToken();
    this.triggers.forEach(trigger => {
      this.totalCallsToMake += trigger.action_ids.length;
    });
    this.triggers.forEach(trigger => {
      asyncForEach(trigger.action_ids, async action => {
        await this.callAction(
          action,
          trigger.product_id,
          trigger.organization_id,
          trigger.action_ids.length
        );
      });
    });
  }

  async triggersFinishedFiring() {
    // eslint-disable-next-line no-console
    console.log('[TC] triggersFinishedFiring — isLastStep:', this.isLastStep, '| hasFailed:', this.hasFailed, '| handoffToken:', this.handoffToken ? `${this.handoffToken.slice(0, 8)}...` : 'null', '| redirectURLs:', JSON.stringify(this.redirectURLs));
    if (this.isLastStep) {
      const deepLink = getDeepLinkIfPresent();
      // eslint-disable-next-line no-console
      console.log('[TC] isLastStep=true path — deepLink:', deepLink);
      if (deepLink) {
        const token = this.hasFailed
          ? null
          : this.handoffToken || (await requestHandoffToken());
        const tokenParam = token ? `&token=${token}` : '';
        const status = this.hasFailed || !token ? 'error' : 'success';
        const redirectTarget = `${deepLink}?status=${status}${tokenParam}`;
        // eslint-disable-next-line no-console
        console.log('[TC] FIRING deep link (isLastStep):', redirectTarget);
        sessionStorage.removeItem(HANDOFF_TOKEN_CACHE_KEY);
        removeSignupRedirect();
        window.location.href = redirectTarget;
        return;
      }
    }
    if (this.redirectURLs.length) {
      const fallbackUrl = this.redirectURLs[this.redirectURLs.length - 1];
      const deepLink = getDeepLinkIfPresent();
      // eslint-disable-next-line no-console
      console.log('[TC] redirectURLs path — fallbackUrl:', fallbackUrl, '| storedDeepLink:', deepLink);
      if (deepLink) {
        const token = this.hasFailed
          ? null
          : this.handoffToken || (await requestHandoffToken());
        const tokenParam = token ? `&token=${token}` : '';
        const status = this.hasFailed || !token ? 'error' : 'success';
        const redirectTarget = `${deepLink}?status=${status}${tokenParam}`;
        // eslint-disable-next-line no-console
        console.log('[TC] FIRING stored deep link (redirectURLs path):', redirectTarget);
        window.location.href = redirectTarget;
        return;
      }
      // Non-WebView flow: follow the backend redirect URL.
      if (isTrustedDeepLink(fallbackUrl)) {
        if (this.hasFailed) {
          // eslint-disable-next-line no-console
          console.log('[TC] FIRING fallbackUrl (trusted deep link, hasFailed):', `${fallbackUrl}?status=error`);
          window.location.href = `${fallbackUrl}?status=error`;
          return;
        }
        const token = this.handoffToken || (await requestHandoffToken());
        if (token) {
          // eslint-disable-next-line no-console
          console.log('[TC] FIRING fallbackUrl (trusted deep link, with token):', `${fallbackUrl}?status=success&token=...`);
          window.location.href = `${fallbackUrl}?status=success&token=${token}`;
          return;
        }
      }
      // eslint-disable-next-line no-console
      console.log('[TC] NAVIGATING to web URL (no deep link stored):', fallbackUrl);
      window.location = fallbackUrl;
    } else {
      // eslint-disable-next-line no-console
      console.log('[TC] triggersFinishedFiring — no redirectURLs and not isLastStep, doing nothing');
    }
  }

  callAction(action, product_id, organization_id) {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async resolve => {
      action = JSON.parse(JSON.stringify(action));
      const parameterRenaming = interactiveConfig.REQUESTS.PARAMETER_RENAMING;
      const parameterValueChange =
        interactiveConfig.REQUESTS.PARAMETER_VALUE_CHANGE;

      if (action) {
        let entity;
        let method;

        // conditions is an array in which we define objects describing a condition
        // objects are treated as an OR condition -> conditions in object 1 must be met OR conditions in object 2
        // keys inside the object are AND condition -> all the conditions defined as keys must be met
        // i.e. {post_test: false}, {user.idp_name: email} -> either not a post test, OR the user signed up with email
        // i.e. {post_test: false, user.idp_name: email} -> not a post test AND user signed up with email

        // ... therefore, if the action has the wait_for_pending_apis flag, with add this flag to each condition object
        action.conditions = action.conditions || [];
        action.conditions = action.conditions.map(condition => {
          if (action.wait_for_pending_apis) {
            condition.has_pending_requests = false;
          }

          return condition;
        });

        // ... if action had no conditions but the flag was still set, make sure to set it now as a condition
        if (!action.conditions.length) {
          action.conditions.push({ has_pending_requests: false });
        }

        action.hidden_inputs = action.params;
        const organizationId = OrganizationService.getOrgId();

        const request = getRequestTypeForActions(action);
        const payload = buildActionPayload(
          [],
          action,
          parameterRenaming,
          parameterValueChange
        );
        const validForCurrentProduct = product_id
          ? product_id === window.product_id
          : true;
        const validForCurrentOrg = organization_id
          ? organization_id === organizationId
          : true;

        entity = request.service;
        method = request.method;

        // eslint-disable-next-line max-len
        if (
          validForCurrentProduct &&
          validForCurrentOrg &&
          (await checkConditionAsync(null, '', action.conditions, true))
        ) {
          callTriggerAction(
            entity,
            method,
            payload,
            action.service_version,
            (redirectURLs, hasError) => {
              if (hasError) {
                this.hasFailed = true;
              }
              if (redirectURLs) {
                this.redirectURLs.push(redirectURLs);
              }

              this.numberOfMadeCalls += 1;
              if (this.totalCallsToMake === this.numberOfMadeCalls) {
                this.triggersFinishedFiring();
              }

              resolve();
            }
          );
        }
      }
    });
  }
}

TriggerComponent.propTypes = {
  triggerActions: PropTypes.object,
};

export default TriggerComponent;
